/**
 * Garage's availability projection as Frontier's asset pickers read it.
 *
 * Frontier does not read the raw inventory (SEC-D34): the units it may assign
 * to a deployment unit come from Garage's governed projection for that unit
 * (`GET /garage/availability-projection/?deployment_unit_id=…`), which only
 * lists compatible, in-stock, unassigned units in the caller's custody and fails
 * closed (`hold`, answered as 503) when its records are not observed recently.
 */
export const AVAILABILITY_CONTRACT =
  "sentinels.garage-availability-projection/v1";

export interface PickerAsset {
  id: string;
  serial_number: string | null;
  qr_code: string | null;
  /** The stock location's code. */
  warehouse_loc: string | null;
  lifecycle_state: string;
}

export interface AssetAvailability {
  /** `unknown` when Garage holds the projection; nothing may be picked then. */
  state: "available" | "unavailable" | "unknown";
  assets: PickerAsset[];
  holds: Array<{ code: string; affected_records: number }>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

/** Read a projection body, or an envelope carrying one; `null` for anything else. */
export function normalizeAssetAvailability(
  payload: unknown,
): AssetAvailability | null {
  const body =
    isRecord(payload) && payload.contract_id === AVAILABILITY_CONTRACT
      ? payload
      : isRecord(payload) && isRecord(payload.data)
        ? payload.data
        : null;
  if (!body || body.contract_id !== AVAILABILITY_CONTRACT) return null;
  const availability = isRecord(body.availability) ? body.availability : {};
  const items = Array.isArray(availability.items) ? availability.items : [];
  const assets = items.filter(isRecord).map((item) => {
    const identity = isRecord(item.identity) ? item.identity : {};
    const location = isRecord(item.stock_location) ? item.stock_location : {};
    return {
      id: String(item.asset_id),
      serial_number: text(identity.serial_number),
      qr_code: text(identity.qr_code),
      warehouse_loc: text(location.code),
      lifecycle_state: text(item.lifecycle_state) ?? "in_stock",
    };
  });
  const holds = (Array.isArray(body.holds) ? body.holds : [])
    .filter(isRecord)
    .map((hold) => ({
      code: String(hold.code),
      affected_records: Number(hold.affected_records) || 0,
    }));
  if (body.status === "hold") return { state: "unknown", assets: [], holds };
  return {
    state: assets.length > 0 ? "available" : "unavailable",
    assets,
    holds,
  };
}

/** A held projection arrives as a 503 carrying the projection: a state, not a failure. */
export function heldAssetAvailability(
  error: unknown,
): AssetAvailability | null {
  if (!isRecord(error) || error.isAxiosError !== true) return null;
  const response = error.response;
  if (!isRecord(response) || response.status !== 503) return null;
  return normalizeAssetAvailability(response.data);
}
